import type {SystemMessageSectionOverride} from './session-scope-prompt'

/** One client contribution to the host's `config.systemMessageSections`. */
export type SystemMessageSections = Record<string, SystemMessageSectionOverride>

/**
 * Combine the client's system-prompt contributions into the single
 * `config.systemMessageSections` map `createSession` accepts.
 *
 * Contributions to different sections sit side by side; contributions to the
 * same section have their content appended in argument order, separated by a
 * blank line. A later contributor never replaces an earlier one — the property
 * is additive by contract, and a silent last-write-wins would drop facts the
 * agent has no other way to learn.
 *
 * Returns `undefined` when nothing contributed, so callers can omit the
 * property rather than send an empty map.
 */
export function mergeSystemMessageSections(
  ...contributions: Array<SystemMessageSections | undefined>
): SystemMessageSections | undefined {
  const merged: SystemMessageSections = {}
  for (const contribution of contributions) {
    if (!contribution) continue
    for (const [section, override] of Object.entries(contribution)) {
      const existing = merged[section]
      merged[section] = existing
        ? {action: override.action, content: `${existing.content}\n\n${override.content}`}
        : override
    }
  }
  return Object.keys(merged).length > 0 ? merged : undefined
}
